import { DUPLICATE_KIND } from '../constants/inviteModes.js';

const normalize = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

const nameOf = (person) => normalize(`${person.first_name} ${person.last_name}`);
const emailOf = (person) => normalize(person.email);

/**
 * Possible duplicates by email + name (what the prototype does; the backend Identity
 * Resolution cascade replaces it when the contract exists, ARP-1480). A row is flagged when it
 * has the same name but a different email than (1) an existing member, or (2) an earlier row
 * of the file. Rows with no name are never flagged.
 *
 * @param {Array<{ rowNumber: number, person: Record<string, string> }>} rows
 * @param {Array<{ email: string, name: string }>} members
 * @returns {Map<number, { kind: 'member', member: object } | { kind: 'file', otherRowNumber: number }>}
 */
export function detectDuplicates(rows, members) {
  const found = new Map();
  const firstByName = new Map();
  const membersByName = new Map();
  members.forEach((member) => {
    const key = normalize(member.name);
    if (!membersByName.has(key)) membersByName.set(key, member);
  });

  [...rows]
    .sort((a, b) => a.rowNumber - b.rowNumber)
    .forEach((row) => {
      const name = nameOf(row.person);
      if (!name) return;
      const sameNameMember = membersByName.get(name);
      if (sameNameMember && normalize(sameNameMember.email) !== emailOf(row.person)) {
        found.set(row.rowNumber, { kind: DUPLICATE_KIND.MEMBER, member: sameNameMember });
        return;
      }
      const earlier = firstByName.get(name);
      if (earlier && emailOf(earlier.person) !== emailOf(row.person)) {
        found.set(row.rowNumber, { kind: DUPLICATE_KIND.FILE, otherRowNumber: earlier.rowNumber });
      } else if (!earlier) {
        firstByName.set(name, row);
      }
    });

  return found;
}
